import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, FolderKanban, Calendar, Users, FileText, DollarSign, Clock, AlertTriangle, Eye } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { t } from '@/i18n/ro';
import { getAccessibleProjects, canCreateProject } from '@/services/projects.service';
import {
  Button,
  Card,
  LoadingState,
  EmptyState,
  Badge,
} from '@/components/common';
import type { Project } from '@/types';

// Extended project type with custom fields
interface ProjectWithCustomFields extends Project {
  customFields?: {
    startDate?: string;
    dueDate?: string;
    partileContractante?: string;
    nrInregistrare?: string;
    dataSemnarii?: string;
    durataContract?: string;
    pretContract?: string;
  };
}

export function ProjectsListPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectWithCustomFields[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canCreate, setCanCreate] = useState(false);

  const loadProjects = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [projectsData, createPermission] = await Promise.all([
        getAccessibleProjects(),
        canCreateProject(),
      ]);

      setProjects(projectsData as ProjectWithCustomFields[]);
      setCanCreate(createPermission);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const handleProjectClick = (project: Project) => {
    navigate(`/projects/${project.id}`);
  };

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('projects.title')}
          </h1>
          <p className="text-gray-600 mt-1">
            {projects.length} {projects.length === 1 ? 'proiect' : 'proiecte'}
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadProjects}
          >
            {t('common.refresh')}
          </Button>
          {canCreate && (
            <Button
              variant="primary"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate('/projects/new')}
            >
              {t('projects.addProject')}
            </Button>
          )}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* Projects grid */}
      {projects.length === 0 ? (
        <EmptyState
          icon="folder"
          title={t('projects.noProjects')}
          description="Nu sunteți membru în niciun proiect momentan."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => handleProjectClick(project)}
            />
          ))}
        </div>
      )}

    </div>
  );
}

// Componenta pentru cardul de proiect
interface ProjectCardProps {
  project: ProjectWithCustomFields;
  onClick: () => void;
}

function ProjectCard({ project, onClick }: ProjectCardProps) {
  const statusName = project._links.status?.title || '';
  const customFields = project.customFields || {};

  // Format date helper
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: ro });
    } catch {
      return dateStr;
    }
  };

  // Format price helper
  const formatPrice = (price?: string) => {
    if (!price) return null;
    const num = parseFloat(price);
    if (isNaN(num)) return price;
    return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(num);
  };

  // Build registration info string
  const registrationInfo = [
    customFields.nrInregistrare,
    formatDate(customFields.dataSemnarii)
  ].filter(Boolean).join(' / ');

  return (
    <Card
      hover
      onClick={onClick}
      className={`group ${project.is_urgent ? 'border-l-4 border-danger-500 bg-danger-50/40' : ''}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          project.is_urgent ? 'bg-danger-100' : 'bg-primary-100'
        }`}>
          <FolderKanban className={`w-6 h-6 ${project.is_urgent ? 'text-danger-600' : 'text-primary-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 group-hover:text-primary-700 transition-colors truncate">
              {project.name}
            </h3>
            {project.is_urgent && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-danger-100 text-danger-700 border border-danger-200 flex-shrink-0"
                title="Proiect marcat ca urgent"
              >
                <AlertTriangle className="w-3 h-3" />
                Urgent
              </span>
            )}
            {project.is_following && (
              <Eye className="w-4 h-4 text-primary-500 flex-shrink-0" aria-label="Urmărit" />
            )}
          </div>
          {/* Nr înregistrare / Data semnării sub nume */}
          {registrationInfo && (
            <p className="text-sm text-primary-600 font-medium mt-0.5">
              {registrationInfo}
            </p>
          )}
          {project.description?.raw && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {project.description.raw}
            </p>
          )}
        </div>
      </div>

      {/* Contract details */}
      <div className="mt-4 space-y-2">
        {customFields.partileContractante && (
          <div className="flex items-start gap-2 text-sm">
            <Users className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <span className="text-gray-600 line-clamp-2">{customFields.partileContractante}</span>
          </div>
        )}
        {customFields.pretContract && (
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-gray-900 font-medium">{formatPrice(customFields.pretContract)}</span>
          </div>
        )}
        {customFields.dueDate && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-gray-600">Data limită: <span className="font-medium">{formatDate(customFields.dueDate)}</span></span>
          </div>
        )}
      </div>

      {/* Status și info */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {statusName && (
          <Badge variant="info" size="sm">
            {statusName}
          </Badge>
        )}
        {project.public && (
          <Badge variant="default" size="sm">
            Public
          </Badge>
        )}
      </div>

      {/* Meta info */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
        {project.createdAt && (
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            <span>
              Creat: {format(parseISO(project.createdAt), 'dd MMM yyyy', { locale: ro })}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}
